// The card's aggregator: the reader's accuracy in each scanned game of the
// period (`GameFacts.accuracy`, from the scan), in time order, and their
// average. Registered with the game store by this plugin's
// setup; the store runs it over the last 30 days' games on every change and
// keeps the result.
import { aggregatorId, average, collect, type Accumulator, type Aggregator, type GameOutcome, type TimeClass, type TimeSlip } from '@chess/api-model';

/** One scanned game's accuracy. */
export interface AccuracyPoint {
  /** The game's id, a stable key. */
  id: string;
  /** Epoch milliseconds. */
  at: number;
  /** 0–100. */
  accuracy: number;
  outcome: GameOutcome;
  timeClass?: TimeClass;
  opponent?: string;
  /** The game's review address, for a click on its point. */
  reviewPath?: string;
  /** The reader let a winning position slip with under a minute on the clock (`GameFacts.timeSlip`). */
  timeSlip?: TimeSlip;
}

/** What the card shows; plain data, so the card and its stories need no store. */
export interface AccuracyTrendResult {
  /** Oldest first. */
  points: AccuracyPoint[];
  /** The mean of the points' accuracies; undefined without any. */
  average?: number;
}

export class AccuracyTrendAggregator implements Aggregator<AccuracyTrendResult> {
  readonly id = aggregatorId('accuracy-trend');

  start(): Accumulator<AccuracyTrendResult> {
    return collect((games) => {
      const points = games
        .filter((g) => g.scanned && g.accuracy !== undefined)
        .sort((a, b) => a.playedAt - b.playedAt)
        .map<AccuracyPoint>((g) => ({
          id: g.id,
          at: g.playedAt,
          accuracy: g.accuracy!,
          outcome: g.outcome,
          timeClass: g.timeClass,
          opponent: g.opponent,
          reviewPath: g.reviewPath,
          ...(g.timeSlip ? { timeSlip: g.timeSlip } : {}),
        }));
      return { points, average: average(points.map((p) => p.accuracy)) };
    });
  }
}
